import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

/**
 * One column of the diagnosis > treatment > procedure catalogue.
 *
 * The three columns carried their own copies of the same card shell, header,
 * search box, loading skeleton, empty state and scroll container - 4 scroll
 * areas, 3 skeletons and 4 empty states across a 645-line template, each
 * slightly different from the others. Only the Diagnostics column ever got a
 * search box, which is why 884 procedures were browse-only.
 *
 * Everything except the ROWS lives here. Rows differ genuinely per column
 * (links popover, provider badges, procedure menu), so they stay with the
 * caller and project in.
 */
@Component({
  selector: 'app-catalogue-pane',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  template: `
    <div class="flex flex-col flex-1 h-full min-h-0 bg-white border border-slate-100 rounded-[2rem] overflow-hidden shadow-sm">
      <div class="bg-slate-50/50 px-4 py-4 flex flex-col gap-3 border-b border-slate-100">
        <!-- Only the TITLE dims when the column has no selection to act on. The
             header itself must not, because it can hold actions that stay live
             in exactly that state. -->
        <h3
          class="font-bold text-slate-800 text-sm uppercase tracking-wider flex items-center gap-2 min-w-0 transition-all"
          [class.opacity-50]="dim">
          <i class="pi {{ icon }} text-primary opacity-70"></i>
          <span class="truncate">{{ heading | translate }}</span>
          <span *ngIf="showCount"
            class="shrink-0 text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full ml-1">{{ count }}</span>
        </h3>

        <!-- Fixed height so all three headers line up: Treatments carries three
             buttons and was wrapping to a second row, which pushed its search
             box and list out of alignment with the other two columns. -->
        <div class="min-h-[2.25rem] flex items-center">
          <ng-content select="[paneActions]"></ng-content>
        </div>
      </div>

      <div *ngIf="showSearch" class="px-4 pt-3 pb-1">
        <div class="relative">
          <i class="pi pi-search absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
          <input type="text" [ngModel]="search" (ngModelChange)="searchChange.emit($event)"
            [placeholder]="searchPlaceholder | translate" [disabled]="searchDisabled"
            class="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-medium disabled:opacity-50">
        </div>
      </div>

      <!-- min-h-0 is what lets this scroll inside a grid row instead of growing
           the row and pushing the page down. -->
      <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar" [ngClass]="bodyClass">
        <div *ngIf="loading" class="flex flex-col gap-3 p-3">
          <div *ngFor="let i of [1, 2, 3, 4, 5]" class="h-14 bg-slate-50 animate-pulse rounded-2xl w-full"></div>
        </div>

        <div *ngIf="!loading && isEmpty"
          class="flex flex-col items-center justify-center py-20 text-slate-400 gap-4">
          <div class="w-16 h-16 rounded-[2rem] bg-slate-50 flex items-center justify-center">
            <i class="pi {{ emptyIcon }} text-2xl opacity-40"></i>
          </div>
          <span class="text-[10px] font-black uppercase tracking-[0.2em] text-center px-6 leading-relaxed">
            {{ emptyMessage | translate }}
          </span>
        </div>

        <ng-content></ng-content>
      </div>
    </div>
  `,
  styles: [
    `
      /* THE HOST MUST BE SIZED, or nothing below it can be.
         An Angular component host defaults to display:inline and sits between
         the grid item and the card. Without this the card's flex-1 has no flex
         parent to act on and its h-full has no definite height to resolve
         against, so it silently falls back to content height - the card grows
         to fit its list and the "scroll area" never scrolls. Written flat, not
         nested, so it cannot compile away. */
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        height: 100%;
      }
    `,
  ],
})
export class CataloguePaneComponent {
  /**
   * Translate key for the column heading.
   *
   * NOT named `title`: that is a global HTML attribute, so Angular would also
   * leave it on the host element and the browser would render the raw key
   * ("treatments_label") as a native tooltip over the card.
   */
  @Input() heading = '';
  /** PrimeIcons class, e.g. "pi-bookmark". */
  @Input() icon = 'pi-list';
  @Input() count: number | null = null;
  @Input() showCount = false;
  /** Dim the title when the column has nothing selected to act on. */
  @Input() dim = false;

  @Input() loading = false;
  @Input() isEmpty = false;
  @Input() emptyIcon = 'pi-folder-open';
  @Input() emptyMessage = '';

  @Input() showSearch = false;
  @Input() search = '';
  @Input() searchDisabled = false;
  @Input() searchPlaceholder = 'SEARCH';
  @Output() searchChange = new EventEmitter<string>();

  /** Padding for the scroll area. The tree view runs edge to edge, so it passes 'p-0'. */
  @Input() bodyClass = 'p-3';
}
